// @ts-strict-ignore
import React from 'react';
import type { Dayjs } from 'dayjs';
import type { DateRangePickerProps } from '@usealloy/component-library';
import {
  Box,
  DateRangePicker,
  Icon,
  IconName,
  Popover,
  Spinner,
} from '@usealloy/component-library';

import { HeaderText, DatePreview } from '../RuleCard/styles';
import type { RuleBreakdownProps } from '../RuleCard/RuleBreakdown';

import BreakdownChart from './BreakdownChart';
import CardContent from './CardContent';
import type { ChartLevels } from './types';
import { CardContentContainer, StyledCard } from './styles';


const Loader = (
  <CardContentContainer>
    <Spinner />
  </CardContentContainer>
);

const NullContent = (
  <CardContent
    iconName={IconName.EmptyXl}
    title="There are no results for the selected date range"
    text="Please try adjusting the date range or leaving the current
    workflow version active for longer to collect more data on this rule."
  />
);

const ErrorContent = (
  <CardContent
    iconName={IconName.Error}
    title="It’s not you, it’s us"
    text="We were unable to fetch the data. Please try again."
    width={250}
  />
);

type ThresholdsCardProps = {
  breakdownMap: Map<number, number>;
  chartLevels: ChartLevels;
  isLoading?: boolean;
  hasError?: boolean;
  selectedDates: any;
  setSelectedDates: any;
  setSliderPercentile: (arg: number) => void;
  sliderPercentile?: number;
  thresholdOperator: string;
  thresholdType?: RuleBreakdownProps['thresholdType'];
};

const ThresholdsCard: React.FC<ThresholdsCardProps> = ({
  breakdownMap,
  chartLevels,
  hasError = false,
  isLoading,
  selectedDates,
  setSliderPercentile,
  setSelectedDates,
  sliderPercentile,
  thresholdOperator,
  thresholdType,
}) => {
  const getThresholdContent = () => {
    if (isLoading) return Loader;
    if (hasError || sliderPercentile === null) return ErrorContent;
    if (breakdownMap) {
      return (
        <>
          {selectedDates?.length && (
            <div>
              <DatePreview>
                {selectedDates[0].format('MM/DD/YYYY')} - {selectedDates[1].format('MM/DD/YYYY')}
              </DatePreview>
            </div>
          )}
          <BreakdownChart
            breakdownMap={breakdownMap}
            chartLevels={chartLevels}
            sliderPercentile={sliderPercentile}
            setSliderPercentile={setSliderPercentile}
            thresholdOperator={thresholdOperator}
            thresholdType={thresholdType}
          />
        </>
      );
    }
    return NullContent;
  };

  return (
    <StyledCard
      header="Thresholds"
      headerButtons={[
        <Popover
          key="date-range-popover"
          trigger={
            <Icon color="var(--cl-gray-1100)" icon={IconName.Calendar} size="large" />
          }
        >
          <Box alignItems="flex-start" direction="column" gap={2}>
            <HeaderText>Date Range Results</HeaderText>
            <DateRangePicker
              onChange={(dates: [Dayjs, Dayjs]) => setSelectedDates(dates)}
              value={selectedDates as unknown as DateRangePickerProps['value']}
              shouldRenderExtraFooter={false}
            />
          </Box>
        </Popover>,
      ]}
      headerInfo="Moving the slider in this graph will adjust the
      threshold and show the number of evaluations captured"
    >
      {getThresholdContent()}
    </StyledCard>
  );
};

export default ThresholdsCard;
